import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import {
  AliasType,
  CancellationSubType,
  Provider,
  RefundType,
  TransactionStatus,
  TransactionType,
  useInsuredEntityQuery,
  useUpsertMidtermTransaction,
} from '@converge/graphql';

import { CreateMidtermTransactionField } from '@converge/data';
import { CreateMidtermTransactionFieldsSchema } from './midterm-transaction.validation.schema';
import { ModalStep } from '../insured-policies.detail';
import { yupResolver } from '@hookform/resolvers/yup';
import WarningIcon from '@mui/icons-material/Warning';

import {
  activeClaimStepConfig,
  ModalConfigType,
  ModalLabels,
  stepValues,
} from './midterm-transaction-modal';
import { MidtermTransactionModal } from './midterm-transaction-modal';
import { useModalStepper } from './midterm-transaction-modal.hooks';
import { InactiveReinstatementConfirmation } from './inactive-reinstatement-modal';
import { NoActiveClaimsConfirmation } from './active-claims-modal';
import { ReinstatementDetails } from './reinstatement-details-modal';
import { useConvergeSnackBar } from '../../../../contexts';
import { ApolloError } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { PolicyDetailProps } from '../overview';
import { formatDollarAmount, NewDisplayFormatDate } from '@converge/utils';

type MidtermTransactionModalReinstatementProps = {
  onClose: () => void;
  showInactive?: boolean | null;
  policySelected: PolicyDetailProps;
  transactionType: TransactionType;
  showClaimMessage?: boolean;
  hasOpenClaims?: boolean;
  claimsLoading?: boolean;
};

export const MidtermTransactionModalReinstatement = ({
  onClose,
  showInactive,
  policySelected,
  showClaimMessage,
  hasOpenClaims,
  claimsLoading,
}: MidtermTransactionModalReinstatementProps) => {
  const { uuid: policyUuid, referenceId: policyName } = policySelected;
  const { insuredUuid } = useParams<{ insuredUuid: string }>();
  const expirationDate = policySelected.endDate
    ? NewDisplayFormatDate(policySelected.endDate)
    : '—';
  const { data: insuredEntityData } = useInsuredEntityQuery(insuredUuid || '');
  const insuredEntity =
    insuredEntityData?.entities?.edges &&
    insuredEntityData?.entities?.edges[0]?.node;

  const insuredName = insuredEntity?.aliases?.edges.find(
    (edge) =>
      edge.node.sources?.includes(Provider.Primary) &&
      edge.node.type === AliasType.LegalName
  )?.node.name;

  const { notify } = useConvergeSnackBar();
  const [warningChecked, setWarningChecked] = useState(false);
  const [inactiveChecked, setInactiveChecked] = useState(false);
  const isOpen = useMemo(() => policyUuid !== '', [policyUuid]);
  const [isDisabled, setIsDisabled] = useState(true);

  const formMethods = useForm<CreateMidtermTransactionFields>({
    resolver: yupResolver(CreateMidtermTransactionFieldsSchema),
    defaultValues: {},
  });

  const handleOnClose = useCallback(() => {
    formMethods.reset();
    onClose();
    setWarningChecked(false);
    setInactiveChecked(false);
  }, [onClose, formMethods]);

  const firstCancel = policySelected?.midtermTransactions.edges.find(
    (e) =>
      e.node.type === TransactionType.Cancellation &&
      e.node.status === TransactionStatus.Completed
  );
  const effectiveDate = firstCancel?.node.effectiveDate
    ? NewDisplayFormatDate(firstCancel.node.effectiveDate)
    : '—';
  const premium = firstCancel?.node.refundAmount
    ? formatDollarAmount(parseFloat(firstCancel.node.refundAmount))
    : '—';
  const inactiveWarningModal: ModalConfigType = showInactive
    ? {
        [ModalStep.INACTIVE_INFO]: {
          title: (
            <Box className="modal-title warning">
              <WarningIcon fontSize="medium" className="icon" />
              <span style={{ marginTop: '2px' }}>
                Inactive For 90 Days Or More
              </span>
            </Box>
          ),
          content: (
            <InactiveReinstatementConfirmation
              isChecked={inactiveChecked}
              setIsChecked={setInactiveChecked}
              setIsDisabled={setIsDisabled}
            />
          ),
          prevLabel: ModalLabels.CANCEL,
          nextLabel: ModalLabels.CONFIRM,
          nextStep: hasOpenClaims
            ? ModalStep.ACTIVE_CLAIM_INFO
            : ModalStep.REINSTATEMENT_DETAILS,
          height: 300,
        },
      }
    : {};

  const activeClaimModal: ModalConfigType = hasOpenClaims
    ? activeClaimStepConfig(
        <NoActiveClaimsConfirmation
          isChecked={warningChecked}
          setIsChecked={setWarningChecked}
          setIsDisabled={setIsDisabled}
        />,
        ModalStep.REINSTATEMENT_DETAILS
      )
    : {};

  const modalConfig: ModalConfigType = {
    ...inactiveWarningModal,
    ...activeClaimModal,

    [ModalStep.REINSTATEMENT_DETAILS]: {
      title: <Box className="modal-title">Policy Reinstatement</Box>,
      content: (
        <ReinstatementDetails
          insuredName={insuredName || ''}
          policyName={policyName || ''}
          effectiveDate={effectiveDate}
          expirationDate={expirationDate}
          premium={premium}
        />
      ),
      prevLabel: ModalLabels.CANCEL,
      nextLabel: ModalLabels.CONFIRM,
      nextStep: null,
      height: 490,
      width: 600,
    },
  };

  const { upsertMidtermTransaction, loading } = useUpsertMidtermTransaction();

  const onSubmitMidtermTransactionHandler = useCallback(async () => {
    let midtermResponse, midtermResponseErrors;
    try {
      const { data, errors } = await upsertMidtermTransaction({
        variables: {
          input: {
            type: TransactionType.Reinstatement,
            policyUuid: policyUuid,
            status: TransactionStatus.Completed,
          },
        },
      });
      midtermResponse = data;
      midtermResponseErrors = errors;
      console.log(midtermResponse, midtermResponseErrors);
      notify(`Midterm transaction created successfully`, {
        variant: 'success',
      });
      handleOnClose();
    } catch (cause) {
      if (cause instanceof ApolloError) {
        const errorMessage = cause.graphQLErrors[0].message;
        notify(
          `Failed to upsert midterm transaction: ${
            errorMessage || 'No data returned by the API'
          }`,
          {
            variant: 'error',
          }
        );
      }
      console.error(`Failed to upsert midterm transaction: ${cause}`);
    }
  }, [notify, upsertMidtermTransaction, handleOnClose, policyUuid]);

  const createMTTReinstatement = async () => {
    return await onSubmitMidtermTransactionHandler();
  };

  const firstStep =
    (Object.keys(modalConfig)[0] as ModalStep) ||
    ModalStep.REINSTATEMENT_DETAILS;
  const { currentStep, handleNextPage, handleBackPage } = useModalStepper({
    modalConfig,
    firstStep,
    setIsDisabled,
    onClose: handleOnClose,
    onFinalStepSubmit: createMTTReinstatement,
    hasOpenClaims,
  });

  interface CreateMidtermTransactionFields {
    [CreateMidtermTransactionField.cancellationType]:
      | CancellationSubType
      | undefined;
    [CreateMidtermTransactionField.refundType]: RefundType;
    [CreateMidtermTransactionField.notes]: string | undefined;
    [CreateMidtermTransactionField.startDate]: string | undefined;
  }
  const { title, body, prevButtonLabel, nextButtonLabel, height, width } =
    stepValues(currentStep, modalConfig[currentStep]);

  return (
    <MidtermTransactionModal
      isOpen={isOpen}
      formMethods={formMethods}
      onClose={handleOnClose}
      title={title}
      body={body}
      prevButtonLabel={prevButtonLabel}
      nextButtonLabel={nextButtonLabel}
      onBack={handleBackPage}
      onNext={handleNextPage}
      isNextDisabled={isDisabled}
      isLoading={loading}
      height={height}
      width={width}
      showClaimMessage={showClaimMessage}
      claimsLoading={claimsLoading}
    />
  );
};
